// One-plan generation for a recurring invoice or bill plan: one draft for the
// frozen next occurrence, never the global run or a post/send/payment. An
// uncertain response holds the scoped plan until a read-only status check.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  clearPlanGeneratePending,
  clearPlanGenerateRecoveryFor,
  firstHeldPlanInScope,
  firstPendingPlanInScope,
  markPlanGenerateHeld,
  markPlanGeneratePending,
  useAnyHeldPlanGenerate,
  useAnyPendingPlanGenerate,
  useHeldPlanGenerate,
  type PlanGenerateScope,
} from "@/components/plan-generate-recovery";
import {
  createdDraftMessage,
  dayOnly,
  heldPlanNotice,
  notConfirmedMessage,
  occurrenceRequestScope,
  planGenerateDisabledReason,
  replayMessage,
  samePlanGenerateScope,
  scopedMutationBlockedMessage,
  statusAbsentMessage,
  statusCheckFailedMessage,
  statusExistsMessage,
  todayDay,
} from "@/components/plan-generate-helpers";
import {
  generateRecurringBillOccurrence,
  generateRecurringInvoiceOccurrence,
  getRecurringBillOccurrence,
  getRecurringInvoiceOccurrence,
  validateRecurringOccurrenceResult,
  validateRecurringOccurrenceStatus,
} from "@/lib/api";

interface RecurringPlanGenerateSchedule {
  id: string;
  legal_entity_id: string;
  status: string;
  next_run_date?: string;
  currency_code: string;
  lines: { amount_minor: number; tax_cents?: number }[];
}

interface CapturedPlan {
  scheduleId: string;
  legalEntityId: string;
  occurrenceDate: string;
  scope: PlanGenerateScope;
}

export function RecurringPlanGenerate({
  kind,
  schedule,
  partyName,
  scope,
  onChanged,
}: {
  kind: "invoice" | "bill";
  schedule: RecurringPlanGenerateSchedule;
  partyName: string;
  scope: PlanGenerateScope;
  onChanged: () => void;
}) {
  const noun = kind === "invoice" ? "invoice" : "bill";
  const held = useHeldPlanGenerate(scope, schedule.id);
  const heldAny = useAnyHeldPlanGenerate(scope);
  const pendingAny = useAnyPendingPlanGenerate(scope);
  const heldElsewhere = heldAny !== null && heldAny.scheduleId !== schedule.id;
  const pendingElsewhere = pendingAny !== null && pendingAny.scheduleId !== schedule.id;
  const [review, setReview] = useState<{ occurrenceDate: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [document, setDocument] = useState<{ id: string } | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const scheduleIdRef = useRef(schedule.id);
  scheduleIdRef.current = schedule.id;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A different plan or launch scope is a different action; never carry a
  // frozen review or a stale outcome across the change.
  useEffect(() => {
    setReview(null);
    setMessage("");
    setDocument(null);
  }, [schedule.id, scope.organization_id, scope.user_id]);

  const stillCurrent = (captured: CapturedPlan) =>
    mounted.current &&
    scheduleIdRef.current === captured.scheduleId &&
    samePlanGenerateScope(scopeRef.current, captured.scope);

  const nextDay = dayOnly(schedule.next_run_date);
  const status = schedule.status.toLowerCase();
  const due = nextDay !== null && nextDay <= todayDay();
  const subtotal = schedule.lines.reduce(
    (sum, line) => sum + line.amount_minor,
    0,
  );
  const disabledReason = planGenerateDisabledReason({
    held: !!held,
    busy,
    pendingElsewhere,
    heldElsewhere,
    status,
    nextDay,
    due,
  });

  const holdCaptured = (captured: CapturedPlan) =>
    markPlanGenerateHeld({
      organizationId: captured.scope.organization_id,
      userId: captured.scope.user_id,
      scheduleId: captured.scheduleId,
      legalEntityId: captured.legalEntityId,
      occurrenceDate: captured.occurrenceDate,
      kind,
      partyName,
    });

  const beginCaptured = (captured: CapturedPlan, requestKind: "invoice" | "bill") => {
    inFlight.current = true;
    setBusy(true);
    markPlanGeneratePending({
      organizationId: captured.scope.organization_id,
      userId: captured.scope.user_id,
      scheduleId: captured.scheduleId,
      kind: requestKind,
    });
  };

  const openReview = () => {
    if (!nextDay || disabledReason) return;
    setMessage("");
    setDocument(null);
    setReview({ occurrenceDate: nextDay });
  };

  const submit = async () => {
    if (!review || inFlight.current) return;
    // Never POST an open review after another scope plan started/failed one.
    if (firstPendingPlanInScope(scope) || firstHeldPlanInScope(scope)) {
      setReview(null);
      setMessage(scopedMutationBlockedMessage());
      return;
    }
    const captured: CapturedPlan = {
      scheduleId: schedule.id,
      legalEntityId: schedule.legal_entity_id,
      occurrenceDate: review.occurrenceDate,
      scope: { ...scope },
    };
    beginCaptured(captured, kind);
    try {
      const raw =
        kind === "invoice"
          ? await generateRecurringInvoiceOccurrence(
              captured.scheduleId,
              captured.legalEntityId,
              captured.occurrenceDate,
              occurrenceRequestScope(captured.scope),
            )
          : await generateRecurringBillOccurrence(
              captured.scheduleId,
              captured.legalEntityId,
              captured.occurrenceDate,
              occurrenceRequestScope(captured.scope),
            );
      const result = validateRecurringOccurrenceResult(raw, {
        schedule_id: captured.scheduleId,
        occurrence_date: captured.occurrenceDate,
        kind,
      });
      if (!result) {
        holdCaptured(captured);
        if (stillCurrent(captured)) {
          setMessage(notConfirmedMessage(captured.occurrenceDate));
        }
        return;
      }
      clearPlanGenerateRecoveryFor(captured.scope, captured.scheduleId);
      if (stillCurrent(captured)) {
        setDocument({ id: result.document_id });
        setMessage(
          result.created
            ? createdDraftMessage(noun, captured.occurrenceDate)
            : replayMessage(
                noun,
                captured.occurrenceDate,
                result.document_status,
              ),
        );
      }
      if (stillCurrent(captured)) onChanged();
    } catch {
      holdCaptured(captured);
      if (stillCurrent(captured)) {
        setMessage(notConfirmedMessage(captured.occurrenceDate));
      }
    } finally {
      clearPlanGeneratePending(captured.scope, captured.scheduleId);
      inFlight.current = false;
      if (mounted.current) setBusy(false);
      if (stillCurrent(captured)) setReview(null);
    }
  };

  const check = async () => {
    if (!held || inFlight.current) return;
    if (firstPendingPlanInScope(scope)) return;
    const captured: CapturedPlan = {
      scheduleId: held.scheduleId,
      legalEntityId: held.legalEntityId,
      occurrenceDate: held.occurrenceDate,
      scope: {
        organization_id: held.organizationId,
        user_id: held.userId,
      },
    };
    beginCaptured(captured, held.kind);
    try {
      const raw =
        held.kind === "invoice"
          ? await getRecurringInvoiceOccurrence(
              captured.scheduleId,
              captured.legalEntityId,
              captured.occurrenceDate,
              occurrenceRequestScope(captured.scope),
            )
          : await getRecurringBillOccurrence(
              captured.scheduleId,
              captured.legalEntityId,
              captured.occurrenceDate,
              occurrenceRequestScope(captured.scope),
            );
      const current = validateRecurringOccurrenceStatus(raw, {
        schedule_id: captured.scheduleId,
        occurrence_date: captured.occurrenceDate,
        kind: held.kind,
      });
      if (!current) throw new Error("malformed status response");
      if (current.exists) {
        if (!current.document_id || !current.document_status) {
          throw new Error("malformed status response");
        }
        clearPlanGenerateRecoveryFor(captured.scope, captured.scheduleId);
        if (stillCurrent(captured)) {
          setDocument({ id: current.document_id });
          setMessage(
            statusExistsMessage(
              noun,
              captured.occurrenceDate,
              current.document_status,
            ),
          );
        }
      } else {
        clearPlanGenerateRecoveryFor(captured.scope, captured.scheduleId);
        if (stillCurrent(captured)) {
          setMessage(
            statusAbsentMessage(
              noun,
              captured.occurrenceDate,
              dayOnly(current.next_run_date),
            ),
          );
        }
      }
      if (stillCurrent(captured)) onChanged();
    } catch {
      if (stillCurrent(captured)) {
        setMessage(statusCheckFailedMessage());
      }
    } finally {
      clearPlanGeneratePending(captured.scope, captured.scheduleId);
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <div className="grid gap-3" data-testid={`recurring-plan-generate-${kind}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={openReview}
          disabled={disabledReason !== null || review !== null}
          title={
            disabledReason ??
            `Create one draft ${noun} for this plan's next due occurrence`
          }
        >
          Generate this plan
        </Button>
        {held ? (
          <Button
            type="button"
            variant="secondary"
            disabled={busy || pendingElsewhere}
            onClick={() => void check()}
          >
            Check current status
          </Button>
        ) : null}
      </div>

      {held && !message ? (
        <p role="status">{heldPlanNotice(held.occurrenceDate)}</p>
      ) : message ? (
        <p role="status">{message}</p>
      ) : null}

      {document ? (
        kind === "invoice" ? (
          <Link
            to="/invoices/$invoiceId"
            params={{ invoiceId: document.id }}
            search={(previous) => previous}
            className="text-sm underline"
          >
            Open invoice
          </Link>
        ) : (
          <Link
            to="/bills/$billId"
            params={{ billId: document.id }}
            search={(previous) => previous}
            className="text-sm underline"
          >
            Open bill
          </Link>
        )
      ) : null}

      <Dialog
        open={review !== null}
        onOpenChange={(open) => {
          if (!open && !busy) setReview(null);
        }}
        title={`Generate the next draft ${noun}?`}
        description={`Creates one draft ${noun} for ${partyName} for this occurrence only. It does not post to the ledger, send anything, or record a payment.`}
      >
        {review ? (
          <div className="grid gap-4">
            <p>
              {partyName} · next due{" "}
              {new Date(`${review.occurrenceDate}T00:00:00Z`).toLocaleDateString()}
            </p>
            <p>
              Plan subtotal:{" "}
              <span className="font-semibold">
                {formatMoney({
                  cents: subtotal,
                  currency: schedule.currency_code || "USD",
                })}
              </span>{" "}
              <span className="text-sm text-[var(--muted-foreground)]">
                (final tax is worked out when the draft is created)
              </span>
            </p>
            <div className="actions-row">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setReview(null)}
              >
                Cancel
              </Button>
              <Button type="button" disabled={busy} onClick={() => void submit()}>
                {busy ? "Generating…" : `Generate draft ${noun}`}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
